// A small useState that mirrors its value to localStorage as it changes.
// Built for this task's resilience requirement (portal/CLAUDE.md: "an
// upload must survive a dropped connection... design the slow-connection
// states properly" — the same principle applied to plain form state: a
// reload or a dropped connection mid-session must not erase what a guest
// buyer already typed, since re-typing a multi-line request under a bid
// deadline on a phone is exactly the cost this whole app exists to remove).
//
// Deliberately NOT a generic "sync any state to storage" library import —
// this is ~25 lines and the whole point of keeping it local is staying
// inside portal/CLAUDE.md's 120KB budget.

import { useCallback, useState } from 'react';

function readStored(key, initialValue) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? initialValue : JSON.parse(raw);
  } catch {
    // Corrupt JSON, storage disabled, or a locked-down browser context — the
    // caller's own initial value is always a safe fallback.
    return initialValue;
  }
}

// `prepare(stored)` — optional — runs ONCE, inside this hook's own lazy
// `useState` initializer, i.e. during the caller's first render, before any
// child component exists to read a stale value out of a prop. It exists for
// exactly one caller today: pages/GuestPage.jsx and pages/RequestFormPage.jsx
// pass utils/pendingLine.js's mergePendingCatalogItems so a buyer's
// catalog-picker adds are folded into the lines draft before
// RequestLinesForm ever mounts — react-hook-form reads `defaultValues`
// once, at mount, and never re-syncs from a prop change, so merging via a
// post-mount effect (the previous shape, hooks/useConsumePendingLine.js) was
// provably too late: the merge landed in this hook's own state/storage
// correctly, but the already-mounted form never saw it.
//
// `prepare` may have a side effect (draining another storage key) — React
// 18 StrictMode calls a useState initializer twice in development
// specifically to catch impure ones (see
// https://react.dev/reference/react/useState), so this must stay safe to
// call twice in a row. It is: whenever `prepare` returns something other
// than its input, the result is written back to `key` immediately, inside
// the initializer — so a second call (finding nothing left to drain from
// the OTHER key) reads `key` fresh and gets back the already-merged value
// from the first call. Both invocations converge on the same answer, and
// the write is never lost.
export function usePersistedState(key, initialValue, prepare) {
  const [state, setState] = useState(() => {
    const stored = readStored(key, initialValue);
    if (!prepare) return stored;
    const prepared = prepare(stored);
    if (prepared !== stored) {
      try {
        window.localStorage.setItem(key, JSON.stringify(prepared));
      } catch {
        // Non-fatal — the merged value still lives in this render's state;
        // it just won't survive a reload before the next real change.
      }
    }
    return prepared;
  });

  const setPersistedState = useCallback(
    (updater) => {
      setState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        try {
          window.localStorage.setItem(key, JSON.stringify(next));
        } catch {
          // Non-fatal — the value just won't survive a reload this time.
        }
        return next;
      });
    },
    [key]
  );

  const clearPersistedState = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Non-fatal.
    }
    // Reset the in-memory value too, not just storage — a real bug found
    // while reviewing this build: every caller (GuestPage's "change email"/
    // "new request", RequestFormPage's "new request") assumed `clear*()`
    // means "back to blank," but a component that stays mounted across the
    // clear (or remounts and reads a still-stale parent state variable for
    // its own `defaultValues`, since react-hook-form only reads
    // `defaultValues` once, at mount) kept showing the just-submitted
    // values. "New request" was pre-filling with the request that was just
    // submitted.
    setState(initialValue);
  }, [key, initialValue]);

  return [state, setPersistedState, clearPersistedState];
}

export default usePersistedState;
